/**
 * Answering category filters and paging from a file.
 *
 * WHY
 * ---
 * A category-filtered ranking was the most expensive request the site could
 * still make: a live query that scans roughly 150,000 rows to return thirty.
 * About thirty-three of them would spend an entire day's read allowance — so
 * browsing categories for a couple of minutes could take the site down, which
 * is exactly what happened to the rankings twice this week.
 *
 * The baked ranking is 240 deep, so most filters can be answered by filtering
 * an array the device already has.
 *
 * WHERE IT REFUSES
 * ----------------
 * When the filtered result is thinner than what was asked for, this returns
 * null and the API answers instead. That matters: a narrow category may have
 * only a handful of products inside a board's top 240 while having hundreds in
 * the catalogue, and serving the handful would silently answer a different
 * question — the same class of mistake as rendering a failed request as an
 * empty search. Cheap when it can be, correct when it cannot.
 */

import type { Product } from '@/types/product';

/** The shape a baked ranking file carries, as far as this module cares. */
export interface StaticBoardData {
  products: Product[];
  eligible: number;
  catalogTotal?: number;
  categories?: Array<{ value: string; count: number; eligible?: number | null }>;
  [key: string]: unknown;
}

/**
 * Products in a category, matched the way the database matches them.
 *
 * The server filters with `category LIKE '%Meat%'` against the full category
 * path, and SQLite's LIKE is case-insensitive for ASCII. Substring matching on
 * a lower-cased path is the same test; anything else would put different
 * products in the list depending on which path served it.
 */
export function filterByCategory(products: readonly Product[], category: string): Product[] {
  const needle = category.toLowerCase();
  return products.filter((p) => (p.category ?? '').toLowerCase().includes(needle));
}

/** How many the board can rank in this category, when the payload knows. */
export function eligibleForCategory(
  data: StaticBoardData,
  category: string,
): number | null {
  const entry = data.categories?.find((c) => c.value === category);
  if (entry === undefined) return null;
  const n = entry.eligible ?? entry.count;
  return typeof n === 'number' ? n : null;
}

export interface SliceRequest {
  category: string | null;
  offset: number;
  limit: number;
}

export interface SliceResult {
  products: Product[];
  eligible: number;
}

/**
 * Take the requested window out of a baked ranking, or null if it cannot be
 * served honestly.
 *
 * Returns null when the window would be short — because the file has run out
 * rather than because the catalogue has. The caller then asks the API, which
 * knows the difference.
 */
export function sliceStatic(data: StaticBoardData, request: SliceRequest): SliceResult | null {
  const { category, offset, limit } = request;

  const pool =
    category === null ? data.products : filterByCategory(data.products, category);

  const eligible =
    category === null ? data.eligible : (eligibleForCategory(data, category) ?? pool.length);

  const window = pool.slice(offset, offset + limit);

  /*
   * A short window is only trustworthy when the pool is genuinely exhausted.
   *
   * `pool.length < eligible` means the file holds a subset of what the board
   * can rank, so a short answer here is an artefact of the file's depth, not a
   * fact about the catalogue.
   */
  if (window.length < limit && pool.length < eligible) return null;

  // Nothing at all, with more known to exist, is the worst possible answer to
  // serve — it reads as "your filters matched nothing".
  if (window.length === 0 && eligible > 0) return null;

  return { products: window, eligible };
}
